import { useEffect } from 'react';
import { Phone, Calendar, Video } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import FloatingCta from '@/components/FloatingCta';
import SchemaJsonLd from '@/components/SchemaJsonLd';
import CalendarEmbed from '@/components/CalendarEmbed';
import { marketingPages } from '@/data/marketingPages';
import { pageDates } from '@/data/pageDates';
import { applyPageMeta } from '@/lib/pageMeta';

const SITE = 'https://www.morganlegalny.com';

export default function AppointmentPage() {
  const p = marketingPages.appointment;
  const canonical = `${SITE}${p.url}`;
  const dates = pageDates(p.url);
  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      url: canonical,
      name: p.metaTitle,
      description: p.metaDescription,
      isPartOf: { '@id': `${SITE}/#website` },
      datePublished: dates.published,
      dateModified: dates.modified,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ReserveAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: canonical,
        actionPlatform: ['http://schema.org/DesktopWebPlatform', 'http://schema.org/MobileWebPlatform'],
      },
      result: { '@type': 'Reservation', name: 'Free Legal Consultation' },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE}/` },
        { '@type': 'ListItem', position: 2, name: 'Schedule Consultation', item: canonical },
      ],
    },
  ];

  useEffect(() => {
    if (typeof document !== 'undefined') {
      applyPageMeta({ title: p.metaTitle, description: p.metaDescription, url: canonical });
    }
  }, [p, canonical]);

  return (
    <div className="min-h-screen bg-cream">
      <SchemaJsonLd schema={schema} />
      <Navbar />
      <main id="appointment">
        <section className="relative bg-navy pt-28 lg:pt-32 pb-16 lg:pb-20 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden pointer-events-none select-none">
            <span
              className="absolute bottom-0 -right-8 font-display text-[10rem] lg:text-[16rem] font-bold text-white leading-none whitespace-nowrap"
              style={{ opacity: 0.03 }}
            >
              SCHEDULE
            </span>
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav aria-label="Breadcrumb" className="mb-6 font-body text-xs text-white/50 tracking-wide">
              <a href="/" className="hover:text-gold transition-colors">Home</a>
              <span className="mx-2 text-gold/60">/</span>
              <span className="text-white/80">Schedule Consultation</span>
            </nav>
            <div className="max-w-3xl">
              <div className="section-label mb-5 text-gold">{p.heroEyebrow}</div>
              <h1 className="font-display text-white text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold leading-[1.05] mb-6">
                {p.heroHeadline}{' '}
                <span className="text-gold italic font-display">{p.heroHeadlineAccent}</span>
              </h1>
              <p className="font-body text-white/85 text-lg lg:text-xl leading-relaxed mb-8 max-w-2xl">
                {p.heroLead}
              </p>
            </div>
          </div>
        </section>

        {/* Booking widget — MLG OS chat + calendar */}
        <section className="bg-cream py-12 lg:py-20">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
              <aside className="lg:col-span-4">
                <div className="section-label mb-4">Three ways to book</div>
                <h2 className="font-display text-navy text-2xl lg:text-3xl font-bold leading-tight mb-6">
                  Pick whichever is easiest.
                </h2>
                <ul className="space-y-5">
                  <li className="flex items-start gap-3">
                    <div className="w-10 h-10 flex-shrink-0 bg-navy text-gold flex items-center justify-center">
                      <Calendar className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-display text-navy font-semibold mb-0.5">Inline scheduler →</p>
                      <p className="font-body text-navy/70 text-sm">Use the booking panel on the right.</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-10 h-10 flex-shrink-0 bg-navy text-gold flex items-center justify-center">
                      <Phone className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-display text-navy font-semibold mb-0.5">
                        <a href="tel:8885291315" className="hover:text-gold">888-529-1315</a>
                      </p>
                      <p className="font-body text-navy/70 text-sm">We will find the earliest slot that works for you.</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-10 h-10 flex-shrink-0 bg-navy text-gold flex items-center justify-center">
                      <Video className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-display text-navy font-semibold mb-0.5">Video consultation</p>
                      <p className="font-body text-navy/70 text-sm">
                        Zoom or Google Meet — request when you book.
                      </p>
                    </div>
                  </li>
                </ul>
                <p className="font-body text-navy/60 text-xs mt-8 leading-relaxed">
                  Consultations are free and last 30–60 minutes with a senior attorney. We respond within two business
                  days during Monday–Friday, 9:00–18:00 Eastern.
                </p>
              </aside>

              <div className="lg:col-span-8">
                <div className="bg-white border border-navy/10 shadow-xl overflow-hidden">
                  <div className="border-b border-navy/10 px-5 py-3 flex items-center gap-3 bg-navy text-white">
                    <span className="w-2.5 h-2.5 bg-gold rounded-full animate-pulse" aria-hidden />
                    <span className="font-body text-sm font-semibold">Book Your Free Consultation</span>
                  </div>
                  <CalendarEmbed height={760} title="Morgan Legal Group — book your free consultation" />
                </div>
                <p className="text-center font-body text-navy/55 text-xs mt-3">
                  Powered by Morgan Legal Group OS — secure, encrypted, attorney-staffed. Prefer to chat? Use the
                  assistant bubble at the bottom of the page.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Fallback content for users on browsers blocking iframes */}
        <section className="bg-white py-16 lg:py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
            {p.sections.map((sec, i) => (
              <article key={i}>
                {sec.heading ? (
                  <h2 className="font-display text-navy text-2xl lg:text-3xl font-bold mb-4">{sec.heading}</h2>
                ) : null}
                {sec.intro ? (
                  <p className="font-body text-navy/80 text-base lg:text-lg leading-relaxed mb-4 font-medium">
                    {sec.intro}
                  </p>
                ) : null}
                {sec.paragraphs?.map((para, j) => (
                  <p key={j} className="font-body text-navy/75 text-base lg:text-lg leading-relaxed mb-3">
                    {para}
                  </p>
                ))}
                {sec.bullets ? (
                  <ul className="space-y-2 mt-3">
                    {sec.bullets.map((b, k) => (
                      <li key={k} className="font-body text-navy/75 text-base lg:text-lg leading-relaxed pl-5 relative">
                        <span className="absolute left-0 top-3 w-1.5 h-1.5 bg-gold rounded-full" />
                        {b}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </article>
            ))}
          </div>
        </section>

        {p.cta ? (
          <section className="bg-gold py-16 lg:py-20">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <h2 className="font-display text-navy text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-5">
                {p.cta.headline}
              </h2>
              <p className="font-body text-navy/85 text-base lg:text-lg leading-relaxed mb-8 max-w-2xl mx-auto">
                {p.cta.body}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a href="tel:8885291315" className="btn-navy">
                  <Phone className="w-4 h-4" />
                  Call 888-529-1315
                </a>
              </div>
            </div>
          </section>
        ) : null}
      </main>
      <Footer />
      <FloatingCta />
    </div>
  );
}
